// amplify-config has a side-effect import that calls Amplify.configure at
// module load. Anything that uses aws-amplify/auth pulls this in transitively
// (via useAuth.ts), but importing it here too makes the dependency explicit
// and survives any future refactor of the auth hook.
import "./lib/amplify-config";

import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { App } from "./App";
import { RootErrorBoundary } from "./app/boundaries";
import { captureFirstTouch } from "./lib/attribution";
import "./styles/globals.css";

// Before anything routes: the landing URL + referrer are only intact now.
captureFirstTouch();

// In dev, evict any service worker left registered from a prior `vite preview`
// / prod build on this origin. A lingering Workbox SW serves old JS cache-first
// and survives even a hard refresh (its fetch handler isn't bypassed), so code
// changes appear to do nothing. We never register a SW in dev
// (`devOptions.enabled: false`), so unregistering it + clearing its caches here
// is always safe. One reload after eviction loads the fresh shell; it then
// stays clean for the rest of the session.
if (import.meta.env.DEV && "serviceWorker" in navigator) {
  void navigator.serviceWorker.getRegistrations().then(async (regs) => {
    if (regs.length === 0) return;
    await Promise.all(regs.map((r) => r.unregister()));
    if (typeof caches !== "undefined") {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k)));
    }
    location.reload();
  });
}

const root = document.getElementById("root");
if (!root) throw new Error("Missing #root element in index.html");

createRoot(root).render(
  <StrictMode>
    <RootErrorBoundary>
      <App />
    </RootErrorBoundary>
  </StrictMode>,
);
